import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

import type { ItineraryWaypoint, WaypointIcon } from "@mountayaapp/types/api/api";

import { waypointIconSchema } from "../../studio/waypointIcons";
import type { ItinerarySliceState } from "./itinerary";
import { mapWaypoints, renumber } from "./itinerary";
import type { LayersSliceState } from "./layers";

/**
 * waypointEditModeSchema names the two engines a waypoint authoring session
 * arms — `point` drops a fresh waypoint at the next map click, `poi-pick`
 * captures the POI feature under the next click (icon + color + name default
 * from the POI's category contract). Zod-first because the value is shared with
 * the waypoint editBegin tool input and validated when an agent or the UI opens
 * a session.
 */
export const waypointEditModeSchema = z.enum(["point", "poi-pick"]);

/**
 * waypointEditIntentSchema names what a waypoint authoring session is for: a
 * brand-new waypoint (`create`) or an in-place revision of an existing one
 * (`update`). On a cancel of an `update` session the original snapshot is
 * restored; on a `create` cancel the session simply clears.
 */
export const waypointEditIntentSchema = z.enum(["create", "update"]);

/**
 * WaypointEditMode names a waypoint authoring engine, derived from
 * waypointEditModeSchema.
 */
export type WaypointEditMode = z.infer<typeof waypointEditModeSchema>;

/**
 * WaypointEditIntent names a waypoint authoring intent, derived from
 * waypointEditIntentSchema.
 */
export type WaypointEditIntent = z.infer<typeof waypointEditIntentSchema>;

/**
 * waypointPoiSeedSchema is the POI capture context attached to a waypoint draft
 * when its position came from a POI-pick: the OpenMapTiles category that
 * resolved the click, the picked feature's display name from the tile (null
 * when the tile carries none), and the source feature id (null when the tile
 * carries none, e.g. an auto-emitted feature with no stable id). It rides
 * through the commit so the persisted waypoint records its provenance. A
 * `point`-mode draft carries null.
 */
export const waypointPoiSeedSchema = z.object({
  category: z.string(),
  name: z.string().nullable(),
  sourceFeatureId: z.string().nullable(),
});

/**
 * WaypointPoiSeed is the POI capture context attached to a waypoint draft,
 * derived from waypointPoiSeedSchema.
 */
export type WaypointPoiSeed = z.infer<typeof waypointPoiSeedSchema>;

/**
 * waypointDraftSchema is the current authored shape of a waypoint during an
 * edit session: its dropped coordinate (null until the first placement
 * lands), the rendered icon glyph, the marker hex color, the human-edited
 * name, and the optional POI capture seed. Zod-first because the value crosses
 * the tool→store→engine boundary and is asserted at each interactive emit; it
 * derives the WaypointDraft type used everywhere downstream.
 */
export const waypointDraftSchema = z.object({
  color: z.string(),
  icon: waypointIconSchema,
  lngLat: z.tuple([z.number(), z.number()]).nullable(),
  name: z.string(),
  poiSeed: waypointPoiSeedSchema.nullable(),
});

/**
 * WaypointDraft is the in-flight shape of a waypoint authored in a session,
 * derived from waypointDraftSchema.
 */
export type WaypointDraft = z.infer<typeof waypointDraftSchema>;

/**
 * DraftWaypointParams seed the synthetic ItineraryWaypoint a from-scratch
 * waypoint authoring session surfaces in the sidenav row before the first
 * placement lands: id + itinerary scope (so swap-by-id holds) and the draft
 * attributes the row reads (icon, color, name, optional lngLat).
 */
export interface DraftWaypointParams {
  draft: {
    color: string;
    icon: WaypointIcon;
    lngLat: [number, number] | null;
    name: string | null;
  };
  itineraryId: string;
  waypointId: string;
}

/**
 * synthesizeDraftWaypoint builds the placeholder ItineraryWaypoint a
 * from-scratch waypoint authoring session surfaces while no commit has landed
 * yet. The id matches the session's waypointId so the sidenav row keys onto
 * it; icon, color, and name read straight off the draft so the row paints
 * with the user's in-flight choices from the first render. The geojson
 * mirrors the persisted Point when the draft has a coordinate (so a later
 * read can recover the position), or stays null when nothing has been placed
 * — the row never reads geometry, the geojson field is just there to satisfy
 * the persisted shape. Every other required field is the neutral seed the
 * schema's non-null requirements need (revision 0, position 0, the epoch
 * created_at, an empty user reference) until the commit's refetch overwrites
 * the row with the server's authoritative shape.
 */
export const synthesizeDraftWaypoint = (params: DraftWaypointParams): ItineraryWaypoint => ({
  color: params.draft.color,
  created_at: new Date(0),
  created_by: { id: "" },
  description: null,
  geojson: params.draft.lngLat
    ? { coordinates: [params.draft.lngLat[0], params.draft.lngLat[1]], type: "Point" }
    : null,
  icon: params.draft.icon,
  id: params.waypointId,
  itinerary_id: params.itineraryId,
  mode: "point",
  name: params.draft.name,
  position: 0,
  revision: 0,
});

/**
 * WaypointSnapshot is the saved shape an `update` waypoint session captures at
 * begin so a cancel restores the entry verbatim. It mirrors the persisted
 * shape (the ItineraryWaypoint geometry + display attributes) the tool layer
 * reads from the active itinerary; null on a `create` session whose original
 * is the absence of the waypoint.
 */
export interface WaypointSnapshot {
  color: string | null;
  geojson: ItineraryWaypoint["geojson"];
  icon: WaypointIcon | null;
  name: string | null;
}

/**
 * WaypointEditBegin opens a waypoint authoring session: the waypoint id the
 * session is keyed on (a freshly-generated client id on `create`, the existing
 * waypoint id on `update`), the intent (create vs. update), the engine the map
 * seam should arm (a TerraDraw point drop, or a POI-pick), and the initial
 * draft attributes the session opens with — `initialIcon` and `initialColor`
 * default the marker glyph + hue (the activity tint on create, the saved
 * values on update), `initialName` seeds the name field, `initialLngLat` is
 * the position the draft starts at (null on create until the first placement;
 * the saved coordinate on update), and `original` captures the saved shape so
 * a cancel can restore it. The tool layer resolves all five before opening the
 * session — they are not derived inside the store.
 */
export interface WaypointEditBegin {
  initialColor: string;
  initialIcon: WaypointIcon;
  initialLngLat: [number, number] | null;
  initialName: string;
  intent: WaypointEditIntent;
  mode: WaypointEditMode;
  original: WaypointSnapshot | null;
  waypointId: string;
}

/**
 * WaypointEditSession is the active waypoint authoring edit: the waypoint id
 * the session is keyed on, the intent (`create` for a fresh waypoint, `update`
 * for a revision), the engine mode (`point` for a TerraDraw point drop,
 * `poi-pick` for a click-to-capture POI), the saved shape `original` an
 * `update` restores on cancel (null on `create`), the in-flight `draft` the
 * map adapter renders + the side panel reads, and the undo/redo history as
 * past/future stacks of prior draft snapshots. The history shape mirrors the
 * segment session: every draft-mutating action — interactive engine emission
 * AND side-panel attribute change — moves the prior draft onto `past` and
 * clears `future`. `enrichmentSnapshot` + `poiGroupSnapshot` capture
 * `activeEnrichments` and `activePoiGroups` at begin so a commit or cancel
 * restores the user's pre-session view verbatim (the `poi-pick` engine
 * auto-enables every POI group so the user can see the features to click; the
 * two snapshots together are the seam that undoes that on close).
 */
export interface WaypointEditSession {
  draft: WaypointDraft;
  /**
   * enrichmentSnapshot holds the `activeEnrichments` slugs at begin so commit
   * + cancel restore exactly the user's pre-session set. The `poi-pick` engine
   * auto-enables the POIs overlay so the user can see the features the click
   * captures; this snapshot is what closes the loop on session end without
   * relying on the engine to remember whether it enabled the slug.
   */
  enrichmentSnapshot: string[];
  future: WaypointDraft[];
  intent: WaypointEditIntent;
  mode: WaypointEditMode;
  original: WaypointSnapshot | null;
  past: WaypointDraft[];
  /**
   * poiGroupSnapshot holds the `activePoiGroups` ids at begin so commit +
   * cancel restore the user's pre-session POI visibility verbatim. The
   * `poi-pick` engine enables every group so the full POI set is clickable;
   * this snapshot is the partner to enrichmentSnapshot that returns the
   * grouped per-layer view (e.g. only water + parking visible) on session end.
   */
  poiGroupSnapshot: string[];
  waypointId: string;
}

/**
 * WaypointEditSliceState is the active waypoint authoring session.
 */
export interface WaypointEditSliceState {
  /**
   * waypointEditSession is the active waypoint authoring session (null while
   * nothing is being authored). Mutually exclusive with segmentEditSession by
   * tool-layer policy. Kept a distinct type from segmentEditSession on purpose —
   * see the note there for why the two sessions model different concerns rather
   * than one generic shape.
   */
  waypointEditSession: WaypointEditSession | null;
}

/**
 * WaypointEditSliceActions are the waypoint authoring session lifecycle plus the
 * draft history and attribute setters, reached only through the tool bus. The
 * relocated removeWaypoint lives here because removing a waypoint also tears down
 * the authoring session targeting it — it belongs with the session it tears down.
 */
export interface WaypointEditSliceActions {
  /**
   * beginWaypointEdit opens a waypoint authoring session. The tool layer
   * resolves the initial defaults (activity-tinted color, map-pin glyph on
   * `create`; the saved values on `update`) and the `original` snapshot, then
   * dispatches; the store seeds the draft from those inputs. A second open
   * replaces the previous session — mutual exclusion across families is
   * enforced one level up at the tool layer, so the store action itself is
   * trustful.
   */
  beginWaypointEdit: (begin: WaypointEditBegin) => void;
  /**
   * cancelWaypointEdit closes the active waypoint authoring session. On an
   * `update` intent the persisted waypoint is restored from `original`; on a
   * `create` intent the session simply clears. A no-op when no waypoint
   * session is active, so a defensive call from a navigation guard is safe.
   */
  cancelWaypointEdit: () => void;
  /**
   * commitWaypointEdit closes the active waypoint authoring session. The tool
   * layer takes care of the persistence (POST/PATCH) before this action runs;
   * the store action's only job is to clear the session so the engine can tear
   * down. A no-op when no waypoint session is active.
   */
  commitWaypointEdit: () => void;
  /**
   * pushWaypointDraft records the previous draft on `past`, clears `future`,
   * and writes the new draft. The interactive engine emissions
   * (setWaypointDraftPosition) and the side-panel attribute setters
   * (setWaypointDraftIcon/Color/Name) all eventually funnel through this so
   * every meaningful change records a single history entry; the dedicated
   * setters are conveniences that build the next draft and call this. A no-op
   * when no waypoint session is active.
   */
  pushWaypointDraft: (draft: WaypointDraft) => void;
  /**
   * redoWaypointDraft moves the head of `future` back onto the current draft,
   * pushing the prior draft onto `past`. A no-op when no waypoint session is
   * active or nothing is ahead.
   */
  redoWaypointDraft: () => void;
  /**
   * removeWaypoint drops a waypoint from the active itinerary and renumbers the
   * remaining ones, AND clears any waypoint authoring session whose target was
   * just removed — the session would otherwise outlive the waypoint it was
   * authoring against, and the engine would arm a draw/pick seam against a now-
   * deleted entity. It lives on the waypoint-edit slice because the session
   * teardown is the reason the action is more than a plain itinerary patch.
   */
  removeWaypoint: (waypointId: string) => void;
  /**
   * setWaypointDraftColor sets the color attribute on the active waypoint
   * draft. Records a history entry — interactive emissions and side-panel
   * edits share the same path so undo/redo work uniformly across both. A
   * no-op when no waypoint session is active.
   */
  setWaypointDraftColor: (color: string) => void;
  /**
   * setWaypointDraftIcon sets the icon attribute on the active waypoint draft.
   * Records a history entry like every other draft-mutating action.
   */
  setWaypointDraftIcon: (icon: WaypointIcon) => void;
  /**
   * setWaypointDraftName sets the name attribute on the active waypoint draft.
   * Records a history entry like every other draft-mutating action.
   */
  setWaypointDraftName: (name: string) => void;
  /**
   * setWaypointDraftPosition sets the dropped coordinate on the active
   * waypoint draft. The optional `poiSeed` carries POI capture context for a
   * `poi-pick` engine; when present, the tool layer additionally seeds icon +
   * color + name from the POI's category contract (the seeding happens BEFORE
   * this action via the dedicated setters, so a single push-history call
   * records the combined change). Records a history entry.
   */
  setWaypointDraftPosition: (lngLat: [number, number], poiSeed: WaypointPoiSeed | null) => void;
  /**
   * undoWaypointDraft moves the current waypoint draft to `future` and
   * restores the top of `past`. A no-op when no waypoint session is active or
   * nothing is behind.
   */
  undoWaypointDraft: () => void;
}

/**
 * waypointEditInitialState seeds no active waypoint authoring session.
 */
export const waypointEditInitialState: WaypointEditSliceState = {
  waypointEditSession: null,
};

/**
 * createWaypointEditSlice builds the waypoint authoring session lifecycle plus
 * the draft history and attribute setters. beginWaypointEdit clears the
 * itinerary selection and snapshots the pre-session enrichment + POI-group
 * visibility so a commit or cancel restores them; every draft action no-ops when
 * no waypoint session is active.
 */
export const createWaypointEditSlice: StateCreator<
  WaypointEditSliceState &
    WaypointEditSliceActions &
    Pick<ItinerarySliceState, "activeItinerary" | "selection"> &
    Pick<LayersSliceState, "activeEnrichments" | "activePoiGroups">,
  [],
  [],
  WaypointEditSliceActions
> = (set) => ({
  beginWaypointEdit: (begin) =>
    set((state) => ({
      // The waypoint session has no live-segment side channel — there is no
      // elevation panel to surface a projection of a single point — so the
      // dirty tick stays on the segment cycle. The waypoint flow is driven
      // by the engine and the side panel directly.
      selection: null,
      waypointEditSession: {
        draft: {
          color: begin.initialColor,
          icon: begin.initialIcon,
          lngLat: begin.initialLngLat,
          name: begin.initialName,
          poiSeed: null,
        },
        enrichmentSnapshot: [...state.activeEnrichments],
        future: [],
        intent: begin.intent,
        mode: begin.mode,
        original: begin.original,
        past: [],
        // Capture the pre-session POI visibility alongside the enrichment set
        // (both BEFORE any session mutation) so a commit or cancel restores
        // the grouped per-layer view, not just the whole-source overlay.
        poiGroupSnapshot: [...state.activePoiGroups],
        waypointId: begin.waypointId,
      },
    })),

  cancelWaypointEdit: () =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      // An `update` cancel restores the saved waypoint shape from the
      // snapshot taken at begin — the tool layer captured the persisted
      // attributes, so the restore is a direct write back onto the
      // active itinerary. A `create` cancel just closes the session; no
      // waypoint was ever persisted to roll back.
      if (session.intent !== "update" || session.original === null) {
        return { waypointEditSession: null };
      }

      const original = session.original;
      const activeItinerary = mapWaypoints(state.activeItinerary, (waypoints) =>
        waypoints.map((waypoint) =>
          waypoint.id === session.waypointId
            ? {
                ...waypoint,
                color: original.color,
                geojson: original.geojson,
                icon: original.icon,
                name: original.name,
              }
            : waypoint,
        ),
      );

      return { activeItinerary, waypointEditSession: null };
    }),

  commitWaypointEdit: () => set({ waypointEditSession: null }),

  pushWaypointDraft: (draft) =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      return {
        waypointEditSession: {
          ...session,
          draft,
          future: [],
          past: [...session.past, session.draft],
        },
      };
    }),

  redoWaypointDraft: () =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      if (session.future.length === 0) {
        return {};
      }

      const [next, ...remaining] = session.future;
      if (!next) {
        return {};
      }

      return {
        waypointEditSession: {
          ...session,
          draft: next,
          future: remaining,
          past: [...session.past, session.draft],
        },
      };
    }),

  removeWaypoint: (waypointId) =>
    set((state) => {
      const activeItinerary = mapWaypoints(state.activeItinerary, (waypoints) =>
        renumber(waypoints.filter((waypoint) => waypoint.id !== waypointId)),
      );
      // Clear any waypoint authoring session whose target was just removed —
      // the session would otherwise outlive the waypoint it was authoring
      // against, and the engine would arm a draw/pick seam against a now-
      // deleted entity.
      if (state.waypointEditSession?.waypointId !== waypointId) {
        return { activeItinerary };
      }

      return { activeItinerary, waypointEditSession: null };
    }),

  setWaypointDraftColor: (color) =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      return {
        waypointEditSession: {
          ...session,
          draft: { ...session.draft, color },
          future: [],
          past: [...session.past, session.draft],
        },
      };
    }),

  setWaypointDraftIcon: (icon) =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      return {
        waypointEditSession: {
          ...session,
          draft: { ...session.draft, icon },
          future: [],
          past: [...session.past, session.draft],
        },
      };
    }),

  setWaypointDraftName: (name) =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      return {
        waypointEditSession: {
          ...session,
          draft: { ...session.draft, name },
          future: [],
          past: [...session.past, session.draft],
        },
      };
    }),

  setWaypointDraftPosition: (lngLat, poiSeed) =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      return {
        waypointEditSession: {
          ...session,
          draft: { ...session.draft, lngLat, poiSeed },
          future: [],
          past: [...session.past, session.draft],
        },
      };
    }),

  undoWaypointDraft: () =>
    set((state) => {
      const session = state.waypointEditSession;
      if (!session) {
        return {};
      }

      if (session.past.length === 0) {
        return {};
      }

      const previous = session.past[session.past.length - 1];
      if (!previous) {
        return {};
      }

      return {
        waypointEditSession: {
          ...session,
          draft: previous,
          future: [session.draft, ...session.future],
          past: session.past.slice(0, -1),
        },
      };
    }),
});
